import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Chip,
  CardContent,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import api from '../config/axios';
import { formatLocalTimestamp } from '../utils/hackathonFlow';

export default function EventReportPanel() {
  const [organizations, setOrganizations] = useState([]);
  const [events, setEvents] = useState([]);
  const [activities, setActivities] = useState([]);
  const [organizationId, setOrganizationId] = useState('');
  const [eventId, setEventId] = useState('');
  const [activityId, setActivityId] = useState('');
  const [day, setDay] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState('');
  const [report, setReport] = useState(null);
  const [applied, setApplied] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/organizations').then((response) => setOrganizations(response.data || [])).catch(() => setError('Não foi possível carregar as organizações.'));
  }, []);

  useEffect(() => {
    if (!organizationId) {
      setEvents([]);
      return;
    }
    api.get('/events').then((response) => {
      setEvents((response.data || []).filter((event) => String(event.organization_id) === String(organizationId)));
    }).catch(() => setEvents([]));
  }, [organizationId]);

  useEffect(() => {
    if (!eventId) {
      setActivities([]);
      return;
    }
    api.get(`/events/${eventId}/sub-events`)
      .then((response) => setActivities(response.data || []))
      .catch(() => setActivities([]));
  }, [eventId]);

  const queryParams = (format) => ({
    organizationId,
    eventId: eventId || undefined,
    activityId: day ? undefined : (activityId || undefined),
    day: activityId ? undefined : (day || undefined),
    from: from || undefined,
    to: to || undefined,
    status: status || undefined,
    format,
  });

  const downloadBlob = (data, filename) => {
    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const withoutFormat = (params) => {
    if (!params) return null;
    const next = { ...params };
    delete next.format;
    return next;
  };
  const exportReady = Boolean(applied) && JSON.stringify(withoutFormat(queryParams())) === JSON.stringify(withoutFormat(applied));

  const load = async (format) => {
    setError('');
    if (format && !exportReady) return;
    const params = format ? { ...applied, format } : queryParams();
    const suffix = params.day ? `-${params.day}` : (params.activityId ? `-atividade-${params.activityId}` : '');
    try {
      const response = await api.get('/hackathon/admin/event-report', {
        params,
        responseType: format ? 'blob' : 'json',
      });
      if (format) {
        downloadBlob(response.data, `presenca${suffix}.${format}`);
        return;
      }
      setApplied(queryParams());
      setReport(response.data);
    } catch (reason) {
      setError(reason?.response?.data?.message || 'Não foi possível gerar o relatório.');
    }
  };

  const formatDay = (value) => {
    const matched = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return matched ? `${matched[3]}/${matched[2]}/${matched[1]}` : '—';
  };

  const indicators = report?.indicators;

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Card><CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField select fullWidth label="Organização" value={organizationId} onChange={(event) => { setOrganizationId(event.target.value); setEventId(''); }}>
              <MenuItem value="">Selecione</MenuItem>
              {organizations.map((item) => <MenuItem key={item.id} value={String(item.id)}>{item.nome}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField select fullWidth label="Evento" value={eventId} onChange={(event) => setEventId(event.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {events.map((item) => <MenuItem key={item.id} value={String(item.id)}>{item.nome}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField select fullWidth label="Atividade" value={activityId} onChange={(event) => { setActivityId(event.target.value); if (event.target.value) setDay(''); }}>
              <MenuItem value="">Presença geral</MenuItem>
              {activities.map((item) => <MenuItem key={item.id} value={String(item.id)}>{item.nome}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth type="date" label="Dia da presença geral" InputLabelProps={{ shrink: true }} value={day} onChange={(event) => { setDay(event.target.value); if (event.target.value) setActivityId(''); }} />
          </Grid>
          <Grid item xs={12} md={3}><TextField fullWidth type="date" label="De" InputLabelProps={{ shrink: true }} value={from} onChange={(event) => setFrom(event.target.value)} /></Grid>
          <Grid item xs={12} md={3}><TextField fullWidth type="date" label="Até" InputLabelProps={{ shrink: true }} value={to} onChange={(event) => setTo(event.target.value)} /></Grid>
          <Grid item xs={12} md={3}>
            <TextField select fullWidth label="Situação da presença" value={status} onChange={(event) => setStatus(event.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="complete">Presença completa</MenuItem>
              <MenuItem value="check_in_only">Somente entrada</MenuItem>
              <MenuItem value="check_out_only">Somente saída (sem check-in)</MenuItem>
              <MenuItem value="present">Com entrada</MenuItem>
              <MenuItem value="absent">Ausente</MenuItem>
              <MenuItem value="active">Inscrição ativa</MenuItem>
              <MenuItem value="cancelled">Cancelada</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button variant="contained" disabled={!organizationId} onClick={() => load()}>Consultar</Button>
              <Button variant="outlined" disabled={!exportReady} onClick={() => load('csv')}>CSV</Button>
              <Button variant="outlined" disabled={!exportReady} onClick={() => load('xlsx')}>XLSX</Button>
              <Button variant="outlined" disabled={!exportReady} onClick={() => load('pdf')}>PDF</Button>
            </Stack>
          </Grid>
        </Grid>
      </CardContent></Card>
      {indicators ? (
        <Grid container spacing={2}>
          {[
            ['Inscritos', indicators.enrolled],
            ['Com entrada', indicators.withCheckIn],
            ['Com saída', indicators.withCheckOut],
            ['Presença completa', indicators.complete],
            ['Ausentes', indicators.absent],
            ['Comparecimento', `${indicators.attendanceRate}%`],
          ].map(([label, value]) => (
            <Grid item xs={6} md={2} key={label}>
              <Card><CardContent>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="h5">{value}</Typography>
              </CardContent></Card>
            </Grid>
          ))}
        </Grid>
      ) : null}
      {report ? (
        <>
          <Typography variant="h6">Por atividade</Typography>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Atividade</TableCell><TableCell>Inscritos</TableCell><TableCell>Entradas</TableCell><TableCell>Saídas</TableCell><TableCell>Completas</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {report.activities.map((row) => (
                <TableRow key={row.activity}><TableCell>{row.activity}</TableCell><TableCell>{row.enrolled}</TableCell><TableCell>{row.checkIns}</TableCell><TableCell>{row.checkOuts}</TableCell><TableCell>{row.complete}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="h6">Participantes</Typography>
          <Typography variant="body2" color="text.secondary">
            {report.participants.length} linha(s) nesta consulta. O arquivo exportado usa os mesmos filtros.
          </Typography>
          <Table size="small" aria-label="Lista de presença">
            <TableHead><TableRow>
              <TableCell>Nome</TableCell><TableCell>Matrícula</TableCell><TableCell>Evento</TableCell><TableCell>Data</TableCell><TableCell>Atividade</TableCell><TableCell>Entrada</TableCell><TableCell>Saída</TableCell><TableCell>Duração</TableCell><TableCell>Situação</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {report.participants.map((row) => (
                <TableRow key={`${row.email}-${row.subEventId ?? 'geral'}-${row.day || row.activity}-${row.checkInAt || 'sem-entrada'}`}>
                  <TableCell>{row.nome}</TableCell>
                  <TableCell>{row.matricula || '—'}</TableCell>
                  <TableCell>{row.eventName || '—'}</TableCell>
                  <TableCell>{formatDay(row.day)}</TableCell>
                  <TableCell>{row.activity}</TableCell>
                  <TableCell>{formatLocalTimestamp(row.checkInAt)}</TableCell>
                  <TableCell>{formatLocalTimestamp(row.checkOutAt)}</TableCell>
                  <TableCell>{row.result === 'Presença completa' && row.presenceSeconds != null ? `${row.presenceSeconds} s` : '—'}</TableCell>
                  <TableCell>{row.result === 'Saída sem entrada' ? <Chip size="small" color="warning" label="Saída sem entrada" /> : row.result}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      ) : null}
    </Stack>
  );
}
